import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/authService';
import { useForm } from '../hooks/useForm';
import { MOUNTAINS_DATA, TERRITORIES } from '../data/mountains';
import { ALL_COUNTRIES, getStatesForCountry } from '../data/locations';

export default function SignUp() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Get states for selected country
  const availableStates = useMemo(() => {
    return getStatesForCountry(selectedCountry);
  }, [selectedCountry]);

  const form = useForm(
    {
      fullName: '',
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      country: 'United States',
      state: 'California',
      mountainOfInfluence: 'religion',
      territoryId: 'TERR-001',
    },
    async (values) => {
      if (values.password !== values.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);
      try {
        await authService.signup(values.email, values.password, {
          fullName: values.fullName,
          displayName: values.displayName,
          phone: values.phone,
          country: values.country,
          state: values.state,
          mountainOfInfluence: values.mountainOfInfluence,
          territoryId: values.territoryId,
        });
        navigate('/dashboard');
      } catch (err) {
        setError(err.message || 'Failed to sign up');
      } finally {
        setLoading(false);
      }
    }
  );

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 hover:opacity-80 transition">
          <img src="/images/fhni%20logo.png" alt="Fire Hive Network" className="h-10 w-10 object-contain" />
          <h1 className="text-3xl font-black">FIRE HIVE</h1>
        </Link>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
          <h2 className="text-2xl font-bold mb-6">Join the Network</h2>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={form.handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <input
                type="text"
                name="fullName"
                value={form.values.fullName}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Display Name (Username)</label>
              <input
                type="text"
                name="displayName"
                value={form.values.displayName}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                placeholder="Choose your display name (e.g. Prophet_John)"
              />
              <p className="text-xs text-gray-500 mt-1">This is how other users will see you</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={form.values.email}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={form.values.phone}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                placeholder="+234 901 234 5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Country</label>
              <select
                name="country"
                value={form.values.country}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);
                  form.handleChange(e);
                  // Reset state to first available state when country changes
                  const newStates = getStatesForCountry(e.target.value);
                  if (newStates.length > 0 && form.values.state !== newStates[0]) {
                    form.setFieldValue('state', newStates[0]);
                  }
                }}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
              >
                {ALL_COUNTRIES.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                State / Province / Region
              </label>
              <select
                name="state"
                value={form.values.state}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
              >
                {availableStates.length > 0 ? (
                  availableStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))
                ) : (
                  <option value="">No states available</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Mountain of Influence</label>
              <select
                name="mountainOfInfluence"
                value={form.values.mountainOfInfluence}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
              >
                {Object.entries(MOUNTAINS_DATA).map(([key, mountain]) => (
                  <option key={key} value={key}>
                    {mountain.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Territory</label>
              <select
                name="territoryId"
                value={form.values.territoryId}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
              >
                {TERRITORIES.map((territory) => (
                  <option key={territory.id} value={territory.id}>
                    {territory.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.values.password}
                  onChange={form.handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.values.confirmPassword}
                  onChange={form.handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition uppercase"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-6">
            Already have an account?{' '}
            <Link to="/signin" className="text-orange-500 hover:text-orange-400 font-semibold">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
